import {
  Dialog,
  Button as DreamButton,
  ErrorState,
  IconBolt,
  IconCalendar,
  Input,
  Toggle,
  useToast,
} from "@dream/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import type { FileUIPart } from "ai";
import { FileTextIcon, LinkIcon, SparklesIcon, Volume2Icon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { type FormEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import {
  Attachment,
  AttachmentInfo,
  AttachmentPreview,
  AttachmentRemove,
  Attachments,
} from "@/components/ai-elements/attachments";
import { ChainOfThought, ChainOfThoughtHeader } from "@/components/ai-elements/chain-of-thought";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Persona } from "@/components/ai-elements/persona";
import {
  PromptInput,
  PromptInputBody,
  PromptInputButton,
  PromptInputFooter,
  PromptInputHeader,
  type PromptInputMessage,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputAttachments,
} from "@/components/ai-elements/prompt-input";
import { SpeechInput } from "@/components/ai-elements/speech-input";
import { Suggestion } from "@/components/ai-elements/suggestion";
import { ApiError, api, unwrap } from "../api/client.ts";
import { isFigmaPresentationUrl } from "../lib/figma-url.ts";
import { calendarSearch } from "../router.tsx";
import { MoonLine } from "./moon-line.tsx";
import type { ChatLine, MoonStatus } from "./moon-types.ts";

interface MoonTurnResult {
  reply: string;
  transcribedText: string | null;
  pdfFileName: string | null;
  presentationTitle: string | null;
  tools: NonNullable<ChatLine["tools"]>;
  tts: "browser";
}

const SPEAK_KEY = "dream.moon.speak";
const SUGGESTIONS = [
  "Liste os clientes cadastrados. Não crie nada.",
  "O que tem nesta semana no calendário?",
  "Isto é roadmap ou cronograma da semana? Classifique antes de agendar.",
  "Lance o cronograma semanal desta apresentação no calendário do cliente.",
];

function readSpeakPref(): boolean {
  try {
    const moon = localStorage.getItem(SPEAK_KEY);
    if (moon === "1" || moon === "0") return moon === "1";
    return localStorage.getItem("dream.hermes.speak") === "1";
  } catch {
    return false;
  }
}

function MoonPromptAttachments() {
  const attachments = usePromptInputAttachments();
  if (attachments.files.length === 0) return null;
  return (
    <Attachments variant="inline">
      {attachments.files.map((file) => (
        <Attachment data={file} key={file.id} onRemove={() => attachments.remove(file.id)}>
          <AttachmentPreview />
          <AttachmentInfo />
          <AttachmentRemove />
        </Attachment>
      ))}
    </Attachments>
  );
}

function speakPortuguese(text: string): void {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  const trimmed = text.trim();
  if (trimmed === "") return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(trimmed.slice(0, 1200));
  utterance.lang = "pt-BR";
  const voices = window.speechSynthesis.getVoices();
  const chosen =
    voices.find((voice) => voice.lang.toLowerCase() === "pt-br") ??
    voices.find((voice) => voice.lang.toLowerCase().startsWith("pt"));
  if (chosen !== undefined) utterance.voice = chosen;
  window.speechSynthesis.speak(utterance);
}

async function fileFromParts(parts: FileUIPart[]): Promise<File | null> {
  const part =
    parts.find(
      (item) =>
        (item.filename ?? "").toLowerCase().endsWith(".pdf") ||
        item.mediaType === "application/pdf",
    ) ?? parts[0];
  if (part === undefined || typeof part.url !== "string" || part.url === "") return null;
  const response = await fetch(part.url);
  if (!response.ok) return null;
  const blob = await response.blob();
  return new File([blob], part.filename ?? "cronograma.pdf", {
    type: part.mediaType || "application/pdf",
  });
}

async function postMoonTurn(input: {
  messages: Array<{ role: "user" | "assistant"; content: string }>;
  pdf: File | null;
  audio: Blob | null;
  presentationUrl: string | null;
}): Promise<MoonTurnResult> {
  const form = new FormData();
  form.set("messages", JSON.stringify(input.messages));
  if (input.pdf !== null) form.set("pdf", input.pdf, input.pdf.name);
  if (input.presentationUrl !== null && input.presentationUrl.trim() !== "") {
    form.set("presentationUrl", input.presentationUrl.trim());
  }
  if (input.audio !== null) {
    const type = input.audio.type || "audio/webm";
    form.set("audio", new File([input.audio], "nota.webm", { type }));
  }

  let response: Response;
  try {
    response = await fetch("/api/admin/hermes/turn", {
      method: "POST",
      credentials: "include",
      body: form,
      signal: AbortSignal.timeout(240_000),
    });
  } catch (error) {
    const name = error instanceof Error ? error.name : "";
    if (name === "TimeoutError" || name === "AbortError") {
      throw new ApiError(504, {
        code: "MOON_TIMEOUT",
        message: "O Moon demorou demais para ler a apresentação. Tente de novo.",
      });
    }
    throw error;
  }

  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = payload as { code?: string; message?: string } | null;
    throw new ApiError(response.status, {
      code: error?.code ?? "MOON",
      message: error?.message ?? `O Moon falhou (${response.status}).`,
    });
  }
  return payload as MoonTurnResult;
}

function moonSearchFromLocation(searchStr: string): string | undefined {
  const raw = new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr).get(
    "apresentacao",
  );
  const value = raw?.trim() ?? "";
  return value === "" ? undefined : value;
}

const startedPresentations = new Set<string>();

export function MoonPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const speakId = useId();
  const presentationId = useId();
  const [lines, setLines] = useState<ChatLine[]>([]);
  const [draft, setDraft] = useState("");
  const [pdf, setPdf] = useState<File | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [speak, setSpeak] = useState(readSpeakPref);
  const [presentationOpen, setPresentationOpen] = useState(false);
  const [presentationDraft, setPresentationDraft] = useState("");
  const [personaReady, setPersonaReady] = useState(
    () => typeof navigator === "undefined" || !navigator.webdriver,
  );
  const pdfInputRef = useRef<HTMLInputElement>(null);

  const searchStr = useRouterState({ select: (state) => state.location.searchStr });
  const queued = moonSearchFromLocation(searchStr);

  const status = useQuery({
    queryKey: ["moon-status"],
    queryFn: async () => unwrap(await api.api.admin.hermes.status.get()) as MoonStatus,
  });

  useEffect(() => {
    try {
      localStorage.setItem(SPEAK_KEY, speak ? "1" : "0");
    } catch {
      /* ignore quota */
    }
  }, [speak]);

  useEffect(() => {
    return () => window.speechSynthesis.cancel();
  }, []);

  const turn = useMutation({
    mutationFn: postMoonTurn,
    onSuccess: async (result) => {
      setLines((current) => {
        const next = [...current];
        const lastUser = [...next].reverse().find((line) => line.role === "user");
        if (lastUser !== undefined && result.transcribedText) {
          lastUser.transcript = result.transcribedText;
        }
        next.push({
          id: crypto.randomUUID(),
          role: "assistant",
          content: result.reply,
          tools: result.tools,
        });
        return next;
      });
      if (speak) speakPortuguese(result.reply);
      void queryClient.invalidateQueries({ queryKey: ["catalog"] });
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
      if (result.tools.some((tool) => tool.ok && tool.name.startsWith("create_"))) {
        toast.success(
          "Calendário atualizado",
          "Abra o calendário para conferir as datas da semana.",
        );
      }
    },
    onError: (error) => {
      const message = error instanceof ApiError ? error.message : "Tente de novo em instantes.";
      toast.error("Moon não concluiu", message);
    },
  });

  const send = useCallback(
    async (
      audio: Blob | null,
      extras?: { text?: string; presentationUrl?: string; pdf?: File; audioUrl?: string },
    ) => {
      const text = (extras?.text ?? draft).trim();
      const presentationUrl = extras?.presentationUrl?.trim() || null;
      const attached = extras?.pdf ?? pdf;
      if (text === "" && attached === null && audio === null && presentationUrl === null) return;
      if (status.data?.llm !== true) {
        toast.error("Moon desligado", "Falta a chave DeepSeek no servidor.");
        return;
      }

      const label =
        text !== ""
          ? text
          : presentationUrl !== null
            ? `Apresentação: ${presentationUrl}`
            : attached !== null
              ? `PDF: ${attached.name}`
              : "Nota de voz";
      const userLine: ChatLine = {
        id: crypto.randomUUID(),
        role: "user",
        content: label,
        pdfName: attached?.name,
        presentation: presentationUrl ?? undefined,
        audioUrl: extras?.audioUrl,
      };
      const nextLines = [...lines, userLine];
      setLines(nextLines);
      setDraft("");
      setPdf(null);

      const history = nextLines
        .filter((line) => line.content.trim() !== "")
        .map((line) => ({ role: line.role, content: line.content }));

      await turn.mutateAsync({
        messages: history,
        pdf: attached,
        audio,
        presentationUrl,
      });
    },
    [draft, lines, pdf, status.data?.llm, toast, turn],
  );

  useEffect(() => {
    if (queued === undefined) return;
    if (startedPresentations.has(queued)) return;
    if (status.isLoading) return;
    if (status.data?.llm !== true) {
      setPresentationDraft(queued);
      setPresentationOpen(true);
      startedPresentations.add(queued);
      return;
    }
    startedPresentations.add(queued);
    void navigate({ to: "/moon", search: {}, replace: true });
    void send(null, {
      text: `Leia esta apresentação Figma, classifique o que ela é no processo (cronograma da semana, roadmap, briefing ou metodologia) e só lance postagens se for a semana do cliente: ${queued}`,
      presentationUrl: queued,
    });
  }, [navigate, queued, send, status.data?.llm, status.isLoading]);

  async function onPromptSubmit(message: PromptInputMessage, event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const text = message.text.trim() || draft.trim();
    const attached = (await fileFromParts(message.files)) ?? pdf;
    void send(null, {
      text,
      pdf: attached ?? undefined,
      presentationUrl: isFigmaPresentationUrl(text) ? text : undefined,
    });
  }

  function submitPresentation(): void {
    const url = presentationDraft.trim();
    if (!isFigmaPresentationUrl(url)) {
      toast.error("Link inválido", "Cole a URL do Deck ou Slides do Figma.");
      return;
    }
    setPresentationOpen(false);
    void send(null, {
      text: `Leia esta apresentação Figma, classifique o que ela é no processo (cronograma da semana, roadmap, briefing ou metodologia) e só lance postagens se for a semana do cliente: ${url}`,
      presentationUrl: url,
    });
    setPresentationDraft("");
  }

  function onPickPdf(file: File | null): void {
    if (file === null) return;
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Arquivo inválido", "Envie um PDF do cronograma ou do roadmap.");
      return;
    }
    setPdf(file);
  }

  const empty = lines.length === 0 && !turn.isPending;
  const configured = status.data;
  const personaState = recording ? "listening" : turn.isPending ? "thinking" : "idle";

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--color-surface)]" data-page="moon">
      <input
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(event) => {
          onPickPdf(event.target.files?.[0] ?? null);
          event.target.value = "";
        }}
        ref={pdfInputRef}
        type="file"
      />

      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--color-line)] px-4 py-3 md:px-6">
        <div className="flex min-w-0 items-center gap-3">
          {personaReady ? (
            <Persona
              className="size-11 shrink-0"
              onLoadError={() => setPersonaReady(false)}
              state={personaState}
              variant="command"
            />
          ) : (
            <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <SparklesIcon className="size-5" />
            </span>
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[1.125rem] font-bold text-foreground">Moon</h1>
              <span
                className={[
                  "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  configured?.llm === true
                    ? "bg-[var(--dream-success-bg)] text-[var(--dream-success-fg)]"
                    : "bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)]",
                ].join(" ")}
              >
                {configured?.llm === true ? "Pronto" : status.isLoading ? "…" : "Desligado"}
              </span>
            </div>
            <p className="mt-0.5 max-w-xl truncate text-[0.8125rem] text-muted-foreground">
              {configured?.model ?? "DeepSeek"} · lê Figma e PDF, classifica, agenda a semana
            </p>
          </div>
        </div>
        <Link
          className="hidden shrink-0 text-[0.8125rem] font-semibold text-primary hover:underline sm:inline"
          search={calendarSearch()}
          to="/calendario"
        >
          Calendário
        </Link>
      </header>

      {status.isError ? (
        <ErrorState
          message={
            status.error instanceof ApiError ? status.error.message : "Falha ao ler o status."
          }
          onRetry={() => void status.refetch()}
        />
      ) : null}

      {configured !== undefined && configured.llm !== true ? (
        <p className="mx-4 mt-3 rounded-xl border border-[var(--dream-warning-border)] bg-[var(--dream-warning-bg)] px-4 py-3 text-[0.8125rem] text-[var(--dream-warning-fg)] md:mx-6">
          O cérebro ainda não está neste ambiente. Cole a chave DeepSeek no servidor. Decks do Figma
          usam o token do Figma.
        </p>
      ) : null}

      <Conversation className="min-h-0 flex-1">
        <ConversationContent className="mx-auto w-full max-w-2xl gap-5 px-4 py-6 md:px-6">
          {empty ? (
            <div className="flex flex-col items-center gap-6 py-4 text-center">
              <span className="flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <IconBolt className="size-7" />
              </span>
              <div className="max-w-md space-y-2">
                <p className="text-[1.25rem] font-bold leading-snug text-foreground">
                  Mande o cronograma. O Moon agenda.
                </p>
                <p className="text-[0.875rem] leading-6 text-muted-foreground">
                  Cole o Deck, solte um PDF ou fale. O Moon diz se é a semana, um roadmap ou um
                  briefing — e só lança postagens quando for a semana.
                </p>
              </div>
              <div className="grid w-full max-w-lg grid-cols-1 gap-2 sm:grid-cols-3">
                <button
                  className="flex min-h-24 flex-col items-center justify-center gap-2 rounded-2xl border border-[var(--color-line)] bg-[var(--color-surface-subtle)] px-3 py-4 text-[0.8125rem] font-semibold text-[var(--color-ink)] transition-[background-color,border-color,transform] duration-[var(--dream-duration-fast)] hover:border-primary/40 hover:bg-[var(--color-surface)] active:scale-[0.99]"
                  onClick={() => setPresentationOpen(true)}
                  type="button"
                >
                  <LinkIcon className="size-5 text-primary" />
                  Apresentação
                </button>
                <button
                  className="flex min-h-24 flex-col items-center justify-center gap-2 rounded-2xl border border-[var(--color-line)] bg-[var(--color-surface-subtle)] px-3 py-4 text-[0.8125rem] font-semibold text-[var(--color-ink)] transition-[background-color,border-color,transform] duration-[var(--dream-duration-fast)] hover:border-primary/40 hover:bg-[var(--color-surface)] active:scale-[0.99]"
                  onClick={() => pdfInputRef.current?.click()}
                  type="button"
                >
                  <FileTextIcon className="size-5 text-primary" />
                  PDF
                </button>
                <Link
                  className="flex min-h-24 flex-col items-center justify-center gap-2 rounded-2xl border border-[var(--color-line)] bg-[var(--color-surface-subtle)] px-3 py-4 text-[0.8125rem] font-semibold text-[var(--color-ink)] transition-[background-color,border-color] duration-[var(--dream-duration-fast)] hover:border-primary/40 hover:bg-[var(--color-surface)]"
                  search={calendarSearch()}
                  to="/calendario"
                >
                  <IconCalendar className="size-5 text-primary" />
                  Calendário
                </Link>
              </div>
              <div className="flex w-full max-w-lg flex-wrap items-center justify-center gap-2">
                {SUGGESTIONS.map((item) => (
                  <Suggestion
                    key={item}
                    onClick={(value) => {
                      if (value.includes("apresentação")) {
                        setPresentationOpen(true);
                        return;
                      }
                      setDraft(value);
                    }}
                    suggestion={item}
                  />
                ))}
              </div>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              {lines.map((line) => (
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  initial={{ opacity: 0, y: 10 }}
                  key={line.id}
                  transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                >
                  <MoonLine
                    line={line}
                    onOpenCalendar={() =>
                      void navigate({ to: "/calendario", search: calendarSearch() })
                    }
                    onSpeak={(text) => speakPortuguese(text)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          )}

          {turn.isPending ? (
            <ChainOfThought defaultOpen>
              <ChainOfThoughtHeader>Lendo o cronograma</ChainOfThoughtHeader>
            </ChainOfThought>
          ) : null}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      <div className="shrink-0 border-t border-[var(--color-line)] bg-[var(--color-surface-subtle)] px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-6">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-2">
          {pdf !== null ? (
            <Attachments variant="inline">
              <Attachment
                data={{
                  id: pdf.name,
                  type: "file",
                  filename: pdf.name,
                  mediaType: "application/pdf",
                  url: "",
                }}
                onRemove={() => setPdf(null)}
              >
                <AttachmentPreview />
                <AttachmentInfo />
                <AttachmentRemove />
              </Attachment>
            </Attachments>
          ) : null}
          {recordError !== null ? (
            <p className="text-[12px] text-destructive" role="alert">
              {recordError}
            </p>
          ) : null}
          {turn.isError ? (
            <p className="text-[12px] text-destructive" role="alert">
              {turn.error instanceof ApiError
                ? turn.error.message
                : "Não foi possível falar com o Moon."}
            </p>
          ) : null}

          <PromptInput
            accept="application/pdf,.pdf"
            className="rounded-2xl border-0 shadow-none [&_[data-slot=input-group]]:rounded-2xl [&_[data-slot=input-group]]:border-[var(--color-line)] [&_[data-slot=input-group]]:bg-[var(--color-surface)] [&_[data-slot=input-group]]:shadow-sm"
            maxFileSize={8 * 1024 * 1024}
            maxFiles={1}
            onSubmit={(message, event) => void onPromptSubmit(message, event)}
          >
            <PromptInputHeader>
              <MoonPromptAttachments />
            </PromptInputHeader>
            <PromptInputBody>
              <PromptInputTextarea
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Cole o link do Figma ou descreva a semana"
                value={draft}
              />
            </PromptInputBody>
            <PromptInputFooter>
              <PromptInputTools>
                <PromptInputButton
                  aria-label="Anexar PDF"
                  onClick={() => pdfInputRef.current?.click()}
                  title="Anexar PDF"
                >
                  <FileTextIcon className="size-4" />
                </PromptInputButton>
                <PromptInputButton
                  aria-label="Apresentação Figma"
                  onClick={() => setPresentationOpen(true)}
                  title="Apresentação Figma"
                >
                  <LinkIcon className="size-4" />
                </PromptInputButton>
                <SpeechInput
                  aria-label={recording ? "Parar gravação" : "Falar"}
                  className="size-10"
                  disabled={status.data?.stt !== true || turn.isPending}
                  lang="pt-BR"
                  onAudioRecorded={async (blob) => {
                    setRecordError(null);
                    if (blob.size < 800) {
                      setRecordError("Áudio curto demais. Segure o microfone e fale de novo.");
                      return "";
                    }
                    const audioUrl = URL.createObjectURL(blob);
                    await send(blob, { audioUrl });
                    return "";
                  }}
                  onListeningChange={(listening) => {
                    setRecording(listening);
                    if (listening) setRecordError(null);
                  }}
                  size="icon-sm"
                  title="Falar"
                />
              </PromptInputTools>
              <PromptInputSubmit
                aria-label="Enviar"
                disabled={turn.isPending}
                size="sm"
                status={turn.isPending ? "submitted" : "ready"}
              >
                Enviar
              </PromptInputSubmit>
            </PromptInputFooter>
          </PromptInput>

          <div className="flex flex-wrap items-center justify-between gap-3 px-0.5 pt-0.5">
            <label
              className="flex items-center gap-2 text-[13px] text-muted-foreground"
              htmlFor={speakId}
            >
              <Volume2Icon className="size-4 text-primary" />
              Falar respostas
              <Toggle
                aria-label="Falar respostas em português"
                checked={speak}
                id={speakId}
                onCheckedChange={setSpeak}
              />
            </label>
            <p className="text-[12px] text-muted-foreground">
              {recording ? "Ouvindo… clique de novo para enviar." : "Enter envia"}
            </p>
          </div>
        </div>
      </div>

      <Dialog
        description="Cole o link do Deck ou Slides. O Moon lê as lâminas, classifica (semana, roadmap ou briefing) e só agenda o cronograma da semana."
        footer={
          <div className="flex justify-end gap-2">
            <DreamButton
              onClick={() => setPresentationOpen(false)}
              type="button"
              variant="secondary"
            >
              Cancelar
            </DreamButton>
            <DreamButton onClick={submitPresentation} type="button">
              Enviar ao Moon
            </DreamButton>
          </div>
        }
        onOpenChange={setPresentationOpen}
        open={presentationOpen}
        size="sm"
        title="Apresentação"
      >
        <label
          className="mb-1.5 block text-[0.8125rem] font-medium text-[var(--color-ink-secondary)]"
          htmlFor={presentationId}
        >
          Link do Figma
        </label>
        <Input
          autoFocus
          id={presentationId}
          inputMode="url"
          onChange={(event) => setPresentationDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              submitPresentation();
            }
          }}
          placeholder="https://www.figma.com/deck/…"
          type="url"
          value={presentationDraft}
        />
      </Dialog>
    </div>
  );
}

/** Compatível com imports antigos da rota /hermes. */
export const HermesPage = MoonPage;
